import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Edit, FileDown, Plus, Search } from "lucide-react";

interface Transaction {
  id: string;
  date: string;
  description: string;
  category: string;
  type: 'income' | 'expense';
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  reference: string;
}

const mockTransactions: Transaction[] = [
  {
    id: "1",
    date: "2024-03-15",
    description: "Government Conservation Grant Q1",
    category: "Grants",
    type: "income",
    amount: 50000,
    status: "completed",
    reference: "GRN-2024-Q1"
  },
  // Add more mock transactions...
];

export function TransactionsTable() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-4 flex-1">
          <div className="relative w-96">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="grants">Grants</SelectItem>
              <SelectItem value="donations">Donations</SelectItem>
              <SelectItem value="operations">Operations</SelectItem>
              <SelectItem value="conservation">Conservation</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="income">Income</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex items-center gap-2">
            <FileDown className="w-4 h-4" />
            Export
          </Button>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Transaction
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left p-4 font-medium text-gray-500">Date</th>
              <th className="text-left p-4 font-medium text-gray-500">Description</th>
              <th className="text-left p-4 font-medium text-gray-500">Category</th>
              <th className="text-left p-4 font-medium text-gray-500">Type</th>
              <th className="text-right p-4 font-medium text-gray-500">Amount</th>
              <th className="text-left p-4 font-medium text-gray-500">Status</th>
              <th className="text-left p-4 font-medium text-gray-500">Reference</th>
              <th className="text-right p-4 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mockTransactions.map((transaction) => (
              <tr key={transaction.id} className="border-b last:border-b-0">
                <td className="p-4">{new Date(transaction.date).toLocaleDateString()}</td>
                <td className="p-4">{transaction.description}</td>
                <td className="p-4">
                  <Badge variant="secondary">{transaction.category}</Badge>
                </td>
                <td className="p-4">
                  <Badge 
                    variant={transaction.type === 'income' ? 'default' : 'secondary'}
                  >
                    {transaction.type}
                  </Badge>
                </td>
                <td className="p-4 text-right">
                  <span className={transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                    {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toLocaleString()}
                  </span>
                </td>
                <td className="p-4">
                  <Badge 
                    variant={
                      transaction.status === 'completed' ? 'default' : 
                      transaction.status === 'pending' ? 'secondary' : 'destructive'
                    }
                  >
                    {transaction.status}
                  </Badge>
                </td>
                <td className="p-4">{transaction.reference}</td>
                <td className="p-4 text-right">
                  <Button variant="ghost" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}